/**
 * React Query hooks of the Sinistres pages. Every query is scoped by the
 * VEOS environment (`registrySlug`) and session (`sessionId`) of the store —
 * a session switch reloads the page anyway — and disabled without a
 * registry. The claim detail fetches its record and its four tables eagerly
 * and in parallel (one query each), so every tab is ready on first click
 * and the « Pièces » count is known up-front.
 */

import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';

import {
  buildClaimsListParams,
  fetchClaim,
  fetchClaimCharges,
  fetchClaimDocuments,
  fetchClaimPieces,
  fetchClaimStakeholders,
  fetchClaims,
  type ClaimChargeRow,
  type ClaimListRow,
  type ClaimRecord,
  type ClaimsListFilters,
  type ClaimStakeholderRow,
} from '../../api/claimsService';
import type { ErpList } from '../../api/erpProxy';
import { useExtranetAuthStore } from '../../auth/store';
import type { DocumentItem } from '../shared/documentItems';
import type { PieceItem } from '../shared/pieceItems';

/** Data of a claim table never changes under the user's eyes — 1 minute. */
const CLAIMS_STALE_TIME_MS = 60_000;

/** Root of every Sinistres query key. */
export function claimsQueryKey(
  registrySlug: string | null,
  sessionId: number | null,
): readonly unknown[] {
  return ['extranet', 'claims', registrySlug, sessionId];
}

/** Query key of one claim resource (`record`, `stakeholders`, …). */
export function claimQueryKey(
  registrySlug: string | null,
  sessionId: number | null,
  idSin: string,
  resource: 'record' | 'stakeholders' | 'charges' | 'documents' | 'pieces',
): readonly unknown[] {
  return ['extranet', 'claim', registrySlug, sessionId, idSin, resource];
}

function useErpScope() {
  const registrySlug = useExtranetAuthStore((s) => s.registrySlug);
  const sessionId = useExtranetAuthStore((s) => s.sessionId);
  return { registrySlug, sessionId };
}

/**
 * « Mes sinistres » — the claims of the session matching the list toolbar's
 * search (`q`) / status (`etat`) (`WS2_EA_TAB_SINISTRES`, fiche 22989). The
 * key carries the effective request params (blank filters ⇒ the unfiltered
 * key); the previous rows stay on screen while a new search loads.
 */
export function useClaimsList(
  filters: ClaimsListFilters = {},
): UseQueryResult<ErpList<ClaimListRow>> {
  const { registrySlug, sessionId } = useErpScope();
  return useQuery<ErpList<ClaimListRow>>({
    queryKey: [...claimsQueryKey(registrySlug, sessionId), buildClaimsListParams(filters)],
    queryFn: () => fetchClaims(registrySlug, filters),
    enabled: registrySlug !== null,
    staleTime: CLAIMS_STALE_TIME_MS,
    retry: false,
    placeholderData: keepPreviousData,
  });
}

/** The five queries of a claim detail. */
export interface ClaimDetailQueries {
  record: UseQueryResult<ClaimRecord | null>;
  stakeholders: UseQueryResult<ClaimStakeholderRow[]>;
  charges: UseQueryResult<ClaimChargeRow[]>;
  documents: UseQueryResult<DocumentItem[]>;
  pieces: UseQueryResult<PieceItem[]>;
}

/** Eager, parallel fetch of `WS2_EA_FIC_SIN` + the four claim tables. */
export function useClaimDetail(idSin: string): ClaimDetailQueries {
  const { registrySlug, sessionId } = useErpScope();
  const enabled = registrySlug !== null && idSin !== '';
  const common = { enabled, staleTime: CLAIMS_STALE_TIME_MS, retry: false } as const;

  const record = useQuery<ClaimRecord | null>({
    queryKey: claimQueryKey(registrySlug, sessionId, idSin, 'record'),
    queryFn: () => fetchClaim(registrySlug, idSin),
    ...common,
  });
  const stakeholders = useQuery<ClaimStakeholderRow[]>({
    queryKey: claimQueryKey(registrySlug, sessionId, idSin, 'stakeholders'),
    queryFn: () => fetchClaimStakeholders(registrySlug, idSin),
    ...common,
  });
  const charges = useQuery<ClaimChargeRow[]>({
    queryKey: claimQueryKey(registrySlug, sessionId, idSin, 'charges'),
    queryFn: () => fetchClaimCharges(registrySlug, idSin),
    ...common,
  });
  const documents = useQuery<DocumentItem[]>({
    queryKey: claimQueryKey(registrySlug, sessionId, idSin, 'documents'),
    queryFn: () => fetchClaimDocuments(registrySlug, idSin),
    ...common,
  });
  const pieces = useQuery<PieceItem[]>({
    queryKey: claimQueryKey(registrySlug, sessionId, idSin, 'pieces'),
    queryFn: () => fetchClaimPieces(registrySlug, idSin),
    ...common,
  });

  return { record, stakeholders, charges, documents, pieces };
}

/** The loading / error props a card takes from a query. */
export interface QueryCardState {
  loading: boolean;
  error: string | null;
}

/** Map a query to the `loading` / `error` props of a card. */
export function queryCardState(query: UseQueryResult): QueryCardState {
  return { loading: query.isLoading, error: query.error?.message ?? null };
}
